<template>
  <div>
    <!-- DIALOGS -->
    <v-dialog
      v-model="confirmDeleteProjects"
      max-width="290px"
    >
      <v-card>
        <v-card-title>
          Potrdite izbris projektov
        </v-card-title>
        <v-card-text>
          <ul>
            <li
              v-for="projectId in selectedProjects"
              :key="projectId"
            >
              {{ allProjects.find(p => p.project_id === projectId)?.name }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="confirmDeleteProjects = false"
          >
            Prekliči
          </v-btn>
          <v-btn
            color="error"
            @click="deleteSelectedProjects"
          >
            Izbriši
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmArchiveProjects"
      max-width="290px"
    >
      <v-card>
        <v-card-title>
          Potrdite arhiviranje projektov
        </v-card-title>
        <v-card-text>
          <ul>
            <li
              v-for="projectId in selectedProjects"
              :key="projectId"
            >
              {{ allProjects.find(p => p.project_id === projectId)?.name }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="confirmArchiveProjects = false"
          >
            Prekliči
          </v-btn>
          <v-btn
            color="error"
            data-cy="confirm-archive-selected"
            @click="archiveSelectedProjects"
          >
            Arhiviraj
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmUnarchicveProjects"
      max-width="290px"
    >
      <v-card>
        <v-card-title>
          Potrdite prenos projektov iz arhiva v aktivne projekte
        </v-card-title>
        <v-card-text>
          <ul>
            <li
              v-for="projectId in selectedProjects"
              :key="projectId"
            >
              {{ allProjects.find(p => p.project_id === projectId)?.name }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="text"
            @click="confirmUnarchicveProjects = false"
          >
            Prekliči
          </v-btn>
          <v-btn
            color="success"
            data-cy="confirm-unarchive-selected"
            @click="unarchiveSelectedProjects"
          >
            Povrni iz arhiva
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-toolbar color="transparent">
        <v-toolbar-title class="text-headline-medium">
          Projekti
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          v-if="canCreateNewProject"
          prepend-icon="add"
          data-cy="open-create-new-project-dialog"
          @click="createProjectDialog = true"
        >
          Nov projekt
        </v-btn>
      </v-toolbar>

      <v-dialog
        v-model="createProjectDialog"
        persistent
        max-width="600px"
      >
        <v-card>
          <v-card-title>Nov projekt</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="newProject.name"
              name="naziv_projekta"
              label="Naziv projekta"
              autofocus
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="text"
              @click="createProjectDialog = false"
            >
              Prekliči
            </v-btn>
            <v-btn
              id="create_new_project"
              color="primary"
              variant="outlined"
              @click="createNewProject()"
            >
              Ustvari projekt
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-toolbar>
        <v-chip v-if="selectedProjects.length > 0">
          {{ selectedProjects.length }} izbranih
        </v-chip>

        <template v-if="selectedProjects.length > 0">
          <v-btn
            icon="delete"
            variant="text"
            aria-label="Izbriši izbrane projekte"
            @click="confirmDeleteProjects = true"
          />
          <v-btn
            v-if="!filters.showArchived"
            icon="archive"
            variant="text"
            aria-label="Arhiviraj izbrane projekte"
            data-cy="archive-selected"
            @click="confirmArchiveProjects = true"
          />
          <v-btn
            v-else
            icon="unarchive"
            variant="text"
            aria-label="Vrni izbrane projekte iz arhiva"
            data-cy="unarchive-selected"
            @click="confirmUnarchicveProjects = true"
          />
        </template>

        <v-spacer />

        <v-switch
          v-model="filters.showArchived"
          class="mr-4"
          label="Arhivirani"
          data-cy="toggle-show-archived"
          hide-details
        />
      </v-toolbar>

      <v-divider />

      <v-empty-state
        v-if="!projects || projects.length < 1"
        icon="business"
        :title="filters.showArchived ? 'Ni arhiviranih projektov' : 'Ni aktivnih projektov'"
        :text="filters.showArchived ? 'Arhivirani projekti bodo prikazani tukaj.' : 'Ustvarite prvi projekt in začnite voditi gradbeni dnevnik.'"
      >
        <template #actions>
          <v-btn
            v-if="canCreateNewProject && !filters.showArchived"
            id="open_create_first_project_dialog"
            color="primary"
            variant="outlined"
            @click="createProjectDialog = true"
          >
            Ustvari projekt
          </v-btn>
        </template>
      </v-empty-state>

      <v-treeview
        v-else
        v-model="selectedProjects"
        :items="projects"
        item-value="project_id"
        item-title="name"
        select-strategy="classic"
        selectable
        hoverable
      >
        <template #title="{ item }">
          <router-link
            :to="item.url"
            data-cy="project-row"
          >
            {{ item.name }}
          </router-link>
        </template>
      </v-treeview>
    </v-card>
  </div>
</template>

<script>
  import MainView from './MainView';
  import router from '../router';
  import UUID from 'uuid';
  import can_read_project from '../model/user/permissions/can_read_project';
  import can_create_new_project from '@/model/user/permissions/can_create_new_project';

  export default {
    extends: MainView,
    data: () => ({
      createProjectDialog: false,
      headers: [
        { title: 'ID', key: 'id' },
        { title: 'Projekt', key: 'name' }
      ],
      newProject: {
        name: ''
      },
      selectedProjects: [],
      filters: {
        showArchived: false
      },

      // Delete projects dialog
      confirmDeleteProjects: false,

      // Archive projects dialog
      confirmArchiveProjects: false,
      confirmUnarchicveProjects: false,

    }),
    computed: {
      userRole() {
        return this.$store.getters.currentUser.role;
      },
      user() {
        return this.$store.getters.currentUser
      },
      canCreateNewProject() {
        return can_create_new_project(this.user);
      },
      allProjects() {
        return this.$store.state.listOfProjects;
      },
      projects() {
        const currentUser = this.$store.state.currentUser;
        const listOfProjects = this.$store.state.listOfProjects;

        const visibleProjects = listOfProjects
          .filter(p => can_read_project(currentUser, p))
          .filter(p => {
            return this.filters.showArchived ? true === p.archived : !p.archived;
          })
        ;

        const projectsById = new Map(
          visibleProjects.map(project => [project.project_id, project])
        );
        const subprojectIds = new Set();

        visibleProjects.forEach(project => {
          (project.subprojects || []).forEach(subprojectId => {
            if (projectsById.has(subprojectId)) {
              subprojectIds.add(subprojectId);
            }
          });
        });

        const buildProjectTree = (project, ancestorIds = new Set()) => {
          const nextAncestorIds = new Set(ancestorIds);
          nextAncestorIds.add(project.project_id);

          const children = (project.subprojects || [])
            .map(subprojectId => projectsById.get(subprojectId))
            .filter(Boolean)
            .filter(subproject => !nextAncestorIds.has(subproject.project_id))
            .map(subproject => buildProjectTree(subproject, nextAncestorIds))
          ;

          const treeProject = {
            ...project,
            url: '/projects/' + project.project_id,
          };

          if (children.length > 0) {
            treeProject.children = children;
          }

          return treeProject;
        };

        // Only root projects are shown at the top level. Their descendants are
        // built recursively so the tree supports any hierarchy depth.
        return visibleProjects
          .filter(project => !subprojectIds.has(project.project_id))
          .map(project => buildProjectTree(project))
        ;
      }
    },
    async created() {
      this.$store.dispatch('fetchProjects');
    },
    methods: {
      goTo(projectId) {
        const href = '/projects/' + projectId;
        if (this.$route.path !== href) {
          router.push(href);
        }
      },
      devNull() {
        /* Needed to add click handler to some tiles in order to highlight on hover */
        return;
      },
      async createNewProject(name) {
        const projectId = UUID.v1();

        this.createProjectDialog = false;
        await this.$store.dispatch('createNewProject', { name: this.newProject.name, projectId: projectId } );
        this.newProject.name = '';

        this.goTo(projectId);
      },
      async deleteSelectedProjects() {
        await Promise.all(
          this.selectedProjects.map(projectId => this.$store.dispatch('deleteProject', projectId))
        );

        this.selectedProjects      = [];
        this.confirmDeleteProjects = false;
      },
      async archiveSelectedProjects() {
        await Promise.all(
          this.selectedProjects.map(projectId => this.$store.dispatch('archiveProject', projectId))
        );

        this.selectedProjects       = [];
        this.confirmArchiveProjects = false;
      },
      async unarchiveSelectedProjects() {
        await Promise.all(
          this.selectedProjects.map(projectId => this.$store.dispatch('unarchiveProject', projectId))
        );

        this.selectedProjects          = [];
        this.confirmUnarchicveProjects = false;
      }
    }
  }
</script>
